/**
 * common.ts — общая лексика кабинета и его оболочка (WEB-1874, этап 2).
 *
 * ЗАЧЕМ ПЕРВЫМ. Главный способ провалить приёмку локализации — экран, где
 * половина надписей на одном языке, а половина на другом (риск №1 разведки).
 * Ловится это тем, что общие слова («Отмена», «Удалить», «Сохранить» — 730
 * повторяющихся фраз на 2 400 вхождений) переводятся ДО того, как разделы
 * начнут переезжать: тогда любой экран получает общие кнопки уже на своём
 * языке, чей бы раздел его ни рисовал.
 *
 * Сюда же — надписи самой оболочки: меню, шапка, статусы строк таблиц,
 * экран падения, гейт админ-панели. Это порция агента каркаса.
 */
import { defineDictionary } from '../define';

export const commonStrings = defineDictionary(
  {
    // ── Общая лексика (кнопки и подписи, которые встречаются везде) ─────────
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    yes: 'Yes',
    no: 'No',
    back: 'Back',
    next: 'Next',
    close: 'Close',
    done: 'Done',
    error: 'Error',
    loading: 'Loading',

    // ── Меню (орг-режим) ────────────────────────────────────────────────────
    navDashboard: 'Dashboard',
    navProjects: 'Projects',
    navBalance: 'Balance & plan',
    navDomains: 'Domains',
    navAudit: 'Audit',

    // ── Меню (проект-режим) ─────────────────────────────────────────────────
    navFunnels: 'Funnels',
    // WEB-2126 (решение владельца `2122-К13`): у проекта, который берёт деньги
    // сам, воронки нет как сущности — человек собирает квизы, а обёртка
    // остаётся техническим слоем. Раздел тот же, подпись другая.
    navQuizzes: 'Quizzes',
    // WEB-2298 п.1: подсказка у переименованного раздела.
    navQuizzesHint:
      'The section is called “Quizzes” because the project settings say you ' +
      'take payments yourself. The project owner can change it in ' +
      '“Project settings → Payments”.',
    navAbTests: 'A/B tests',
    navAnalytics: 'Analytics',
    navEmailSequences: 'Email sequences',
    navCommerce: 'Subscriptions',
    navSettings: 'Settings',

    // ── Шапка ───────────────────────────────────────────────────────────────
    wholeOrganization: 'Whole organization',
    organization: 'Organization',
    organizations: 'Organizations',
    // WEB-3758: поиск в списке организаций меню профиля (эталон app.jsx:176)
    orgFind: 'Find an organization',
    orgNone: 'Nothing found',
    personalSettings: 'Personal settings',
    // WEB-3764 доводка холста: заголовок капсулы телефона на адресах
    // конструктора (эталон sections/editor.jsx:11, en — «Open the builder», :13).
    builderTitle: 'Builder',
    documentation: 'Documentation',
    signOut: 'Sign out',
    orgBalanceTooltip: 'Organization wallet balance',
    orgBalanceTopUpTooltip: 'Wallet balance — click to top up',

    // ── Оболочка нового вида (WEB-3739): меню профиля и палитра ⌘K ─────────
    // Тексты — из словаря `L` эталона `design/cabinet-rescin/app.jsx`.
    navAiStudio: 'AI studio',
    meBalance: 'Balance',
    palFind: 'Find or ask',
    palPlaceholder: 'Section, setting or a task for AI…',
    palSections: 'Sections',
    palActions: 'Actions and settings',
    palAsk: (q: string) => `Ask AI: «${q}»`,
    palEscKey: 'esc',
    // Подпись раздела справа в строке команды (WEB-3739 доводка 2) — заголовки
    // разделов эталона; «Builder» — из подписи команды «Open the builder».
    palWhereContent: 'Builder',
    palWhereAi: 'AI studio',
    palWhereAudit: 'Audit',
    palWhereProjects: 'Projects',
    palWhereBalance: 'Balance & plan',
    palWhereHome: 'Dashboard',
    palWhereAnalytics: 'Analytics',
    palWhereDash: 'Dashboard',
    palHintNav: '↑↓ select',
    palHintOpen: '↵ open',
    palHintClose: 'esc close',

    // ── Доводка 1 оболочки (WEB-3739): «Первые шаги» в низу меню ────────────
    // Тексты — эталона `design/cabinet-rescin/app.jsx` (`steps`, `stepsOf`, `stepList`).
    firstSteps: 'First steps',
    firstStepsOf: (done: number, total: number) => `${done} of ${total}`,
    firstStepFunnel: 'Create a funnel',
    firstStepPayments: 'Connect payments',
    firstStepDomain: 'Connect a domain',
    firstStepPublish: 'Publish',
    firstStepPurchase: 'First purchase',

    // ── Доводка 1: команды разделов палитры ⌘K (`paletteCommands.ts`) ───────
    // Подписи — поля `commands` разделов эталона `design/cabinet-rescin/sections/*.jsx`.
    cmdHomeAsk: 'Ask about the numbers',
    cmdAnalyticsCountries: 'Report: countries, 30 days',
    cmdAnalyticsCohorts: 'Cohorts and payback',
    cmdEditorOpen: 'Open the builder',
    cmdAiCreate: 'Create a funnel with AI',
    cmdAiLink: 'Build a funnel from an app link',
    cmdAiChat: 'Build a funnel in a chat with AI',
    cmdAiPlan: 'Screen plan before the build',
    cmdAiMode: 'Generation mode: Simple or Pro',
    cmdAiModel: 'AI model for generation',
    cmdAiKind: 'What to create: funnel, quiz or paywall',
    cmdAiLang: 'Funnel language for generation',
    cmdAiNiche: 'Niche and base template',
    cmdAiScreens: 'Number of screens',
    cmdAiEnding: 'Funnel ending',
    cmdAiSample: 'Sample — app screenshot for style',
    cmdAiNoImages: 'No AI images',
    cmdAiContext: 'Project context for AI',
    cmdAiReanalyze: 'Update the store page analysis',
    cmdAiHistory: 'Generation history',
    cmdAiRunning: 'Back to the running generation',
    cmdAiTopup: 'Top up AI credits',
    cmdAuditTeam: 'Project team activity log',
    cmdAuditOrgTeam: 'Organization activity log',
    cmdAuditImportant: 'Important actions only',
    cmdAuditExport: 'Export the log to CSV',
    cmdAuditTraffic: 'Traffic: where the path breaks',
    cmdAuditOrgTraffic: 'Organization traffic',
    cmdAuditTrace: 'Find a visitor path by click_id / guid / email',
    cmdAuditLost: 'Who dropped at a stage',
    cmdOrgDashMoney: 'Dashboard “Money”',
    cmdOrgDashTune: 'Customize the dashboard',
    cmdOrgProjectsList: 'Organization projects',
    cmdOrgProjectsCreate: 'Create project',
    cmdBalancePlan: 'Balance & plan',
    cmdBalanceTopup: 'Top up the balance',
    cmdBalanceSubscription: 'Manage subscription',
    cmdBalanceUsage: 'Exits and monthly bill estimate',
    cmdBalanceRates: 'Rates and per-project breakdown',
    cmdBalanceHistory: 'Transactions',
    cmdBalanceInvoices: 'Invoices',
    cmdBalanceLimit: 'AI spending limit',
    cmdBalanceFromBalance: 'Pay the plan from the balance',
    cmdBalanceEnterprise: 'Enterprise — contact sales',

    // ── Боковое меню и капсула телефона (WEB-3739 юнит 2) ───────────────────
    // Тексты — из словаря `T` эталона `design/cabinet-rescin/sections/shell-x.jsx`.
    shellSecProject: 'Project',
    shellSecManage: 'Manage',
    shellFind: 'Search & AI',
    shellFindProject: 'Find a project…',
    shellNothingFound: 'Nothing found',
    shellPin: 'Pin menu',
    shellUnpin: 'Unpin menu',
    shellOpenMenu: 'Open menu',
    shellSideMenu: 'Side menu',

    // ── Состояние строки в таблицах ─────────────────────────────────────────
    statusDraft: 'Draft',
    statusActive: 'Active',
    statusDeleted: 'Deleted',
    statusInactive: 'Inactive',

    // ── Колонка публичного адреса ───────────────────────────────────────────
    openInNewTab: 'Open in a new tab',
    funnelPausedUrlInactive: 'The funnel is paused, this address is not served',
    bindDomainForPublicLink: 'Bind a custom domain to get a public link',
    bindDomain: 'Connect a domain', // WEB-4127 (сверка 4114): по эталону

    // ── Падение интерфейса ──────────────────────────────────────────────────
    crashTitle: 'Something went wrong',
    crashSubtitle: 'We have already been notified. Try reloading the page.',
    reloadPage: 'Reload the page',

    // ── Гейт панели управления ──────────────────────────────────────────────
    adminAccessDeniedTitle: 'Access denied',
    adminAccessDeniedSubtitle: 'The admin panel is available to administrators only.',
    adminTwoFactorTitle: 'Two-factor protection required',
    adminTwoFactorSubtitle:
      'The admin panel now requires two-factor authentication — without it a stolen password would open the whole business. Turn it on in your account settings; it takes a couple of minutes.',
    adminEnableTwoFactor: 'Turn it on in security settings',

    // ── Одноразовый код ─────────────────────────────────────────────────────
    otpDigitLabel: (position: number) => `Digit ${position}`,

    // ── Массовое удаление ───────────────────────────────────────────────────
    bulkDeleteDone: (ok: number) => `Deleted: ${ok}`,
    bulkDeleteNone: (total: number) => `Could not delete anything (${total})`,
    bulkDeletePartial: (ok: number, total: number, reasons: string) =>
      `Deleted: ${ok} of ${total}. ${reasons}`,
    // WEB-2587: своя фраза двери удаления на отказ «не своё» (NOT_RESOURCE_OWNER);
    // общая фраза кода в реестре нейтральна к действию (utils/localizeError.ts,
    // localizeDeleteError).
    deleteNotOwner:
      'You can only delete what you created yourself. Ask a project owner or admin to delete it for you.',

    // ── Проверка ссылки в поле блока ────────────────────────────────────────
    // WEB-2067 п.3: ровно перечень сервера (url-allowlist.ts) — сверяет тест.
    urlSchemeNotAllowed:
      'The link must start with http://, https:// or mailto:',

    // ── Выход из аккаунта ───────────────────────────────────────────────────
    logoutServerUnreachable:
      'You are signed out on this device, but the server could not be reached.',
  },
  {
    cancel: 'Отмена',
    save: 'Сохранить',
    delete: 'Удалить',
    yes: 'Да',
    no: 'Нет',
    back: 'Назад',
    next: 'Далее',
    close: 'Закрыть',
    done: 'Готово',
    error: 'Ошибка',
    loading: 'Загрузка',

    navDashboard: 'Дашборд',
    navProjects: 'Проекты',
    navBalance: 'Баланс/Тариф',
    navDomains: 'Домены',
    navAudit: 'Аудит',

    navFunnels: 'Воронки',
    navQuizzes: 'Квизы',
    // WEB-2298 п.1: подсказка у переименованного раздела.
    navQuizzesHint:
      'Раздел называется «Квизы», потому что в настройках проекта выбрано ' +
      '«сам, у себя». Изменить может владелец проекта — в «Настройки ' +
      'проекта → Платежи».',
    navAbTests: 'A/B-тесты',
    navAnalytics: 'Аналитика',
    navEmailSequences: 'Email-цепочки',
    navCommerce: 'Подписки',
    navSettings: 'Настройки',

    wholeOrganization: 'Вся организация',
    organization: 'Организация',
    organizations: 'Организации',
    orgFind: 'Найти организацию',
    orgNone: 'Ничего не нашлось',
    personalSettings: 'Личные настройки',
    builderTitle: 'Конструктор',
    documentation: 'Документация',
    signOut: 'Выйти',
    orgBalanceTooltip: 'Баланс кошелька организации',
    orgBalanceTopUpTooltip: 'Баланс кошелька — нажмите, чтобы пополнить',

    navAiStudio: 'ИИ-студия',
    meBalance: 'Баланс',
    palFind: 'Найти или спросить',
    palPlaceholder: 'Раздел, настройка или задача для ИИ…',
    palSections: 'Разделы',
    palActions: 'Действия и настройки',
    palAsk: (q: string) => `Спросить ИИ: «${q}»`,
    palEscKey: 'esc',
    palWhereContent: 'Конструктор',
    palWhereAi: 'ИИ-студия',
    palWhereAudit: 'Аудит',
    palWhereProjects: 'Проекты',
    palWhereBalance: 'Баланс/Тариф',
    palWhereHome: 'Дашборд',
    palWhereAnalytics: 'Аналитика',
    palWhereDash: 'Дашборд',
    palHintNav: '↑↓ выбрать',
    palHintOpen: '↵ открыть',
    palHintClose: 'esc закрыть',

    firstSteps: 'Первые шаги',
    firstStepsOf: (done: number, total: number) => `${done} из ${total}`,
    firstStepFunnel: 'Создать воронку',
    firstStepPayments: 'Подключить оплату',
    firstStepDomain: 'Подключить домен',
    firstStepPublish: 'Опубликовать',
    firstStepPurchase: 'Первая покупка',

    cmdHomeAsk: 'Спросить про цифры',
    cmdAnalyticsCountries: 'Отчёт: страны за 30 дней',
    cmdAnalyticsCohorts: 'Когорты и окупаемость',
    cmdEditorOpen: 'Открыть конструктор',
    cmdAiCreate: 'Создать воронку с ИИ',
    cmdAiLink: 'Собрать воронку по ссылке на приложение',
    cmdAiChat: 'Собрать воронку в разговоре с ИИ',
    cmdAiPlan: 'План экранов перед сборкой',
    cmdAiMode: 'Режим генерации: простой или Pro',
    cmdAiModel: 'Модель ИИ для генерации',
    cmdAiKind: 'Что создаём: воронка, квиз или пейволл',
    cmdAiLang: 'Язык воронки при генерации',
    cmdAiNiche: 'Тематика и шаблон-основа',
    cmdAiScreens: 'Количество экранов',
    cmdAiEnding: 'Что в конце воронки',
    cmdAiSample: 'Образец — скриншот приложения для стиля',
    cmdAiNoImages: 'Без ИИ-картинок',
    cmdAiContext: 'Контекст проекта для ИИ',
    cmdAiReanalyze: 'Обновить анализ стор-страницы',
    cmdAiHistory: 'История генераций',
    cmdAiRunning: 'Вернуться к идущей генерации',
    cmdAiTopup: 'Пополнить кредиты для ИИ',
    cmdAuditTeam: 'Журнал действий команды проекта',
    cmdAuditOrgTeam: 'Журнал действий организации',
    cmdAuditImportant: 'Только важные действия в журнале',
    cmdAuditExport: 'Выгрузить журнал в CSV',
    cmdAuditTraffic: 'Трафик: где обрывается путь',
    cmdAuditOrgTraffic: 'Трафик организации',
    cmdAuditTrace: 'Найти путь посетителя по click_id / guid / email',
    cmdAuditLost: 'Кто оборвался на стадии',
    cmdOrgDashMoney: 'Дашборд «Деньги»',
    cmdOrgDashTune: 'Настроить дашборд',
    cmdOrgProjectsList: 'Проекты организации',
    cmdOrgProjectsCreate: 'Создать проект',
    cmdBalancePlan: 'Баланс и тариф',
    cmdBalanceTopup: 'Пополнить баланс',
    cmdBalanceSubscription: 'Управление подпиской',
    cmdBalanceUsage: 'Переходы и оценка счёта за месяц',
    cmdBalanceRates: 'Ставки и разбивка по проектам',
    cmdBalanceHistory: 'История операций',
    cmdBalanceInvoices: 'Счета',
    cmdBalanceLimit: 'Лимит трат на ИИ',
    cmdBalanceFromBalance: 'Оплачивать тариф с баланса',
    cmdBalanceEnterprise: 'Enterprise — отдел продаж',

    shellSecProject: 'Проект',
    shellSecManage: 'Управление',
    shellFind: 'Поиск и ИИ',
    shellFindProject: 'Найти проект…',
    shellNothingFound: 'Ничего не найдено',
    shellPin: 'Закрепить меню',
    shellUnpin: 'Открепить меню',
    shellOpenMenu: 'Открыть меню',
    shellSideMenu: 'Боковое меню',

    statusDraft: 'Черновик',
    statusActive: 'Активен',
    statusDeleted: 'Удалён',
    statusInactive: 'Неактивен',

    openInNewTab: 'Открыть в новой вкладке',
    funnelPausedUrlInactive: 'Воронка на паузе, адрес не обслуживается',
    bindDomainForPublicLink: 'Для публичной ссылки привяжите кастомный домен',
    bindDomain: 'Привяжите домен',

    crashTitle: 'Что-то пошло не так',
    crashSubtitle:
      'Мы уже получили информацию об ошибке. Попробуйте обновить страницу.',
    reloadPage: 'Обновить страницу',

    adminAccessDeniedTitle: 'Доступ запрещён',
    adminAccessDeniedSubtitle: 'Панель управления доступна только администраторам.',
    adminTwoFactorTitle: 'Нужна двухфакторная защита',
    adminTwoFactorSubtitle:
      'Панель управления теперь требует двухфакторную аутентификацию — без неё украденный пароль давал бы доступ ко всему бизнесу. Включите её в настройках аккаунта — это займёт пару минут.',
    adminEnableTwoFactor: 'Включить в настройках безопасности',

    otpDigitLabel: (position: number) => `Цифра ${position}`,

    bulkDeleteDone: (ok: number) => `Удалено: ${ok}`,
    bulkDeleteNone: (total: number) => `Не удалось удалить ни один объект (${total})`,
    bulkDeletePartial: (ok: number, total: number, reasons: string) =>
      `Удалено: ${ok} из ${total}. ${reasons}`,
    deleteNotOwner:
      'Удалить можно только то, что вы создали сами. Попросите владельца или администратора проекта удалить это за вас.',

    // WEB-2067 п.3: ровно перечень сервера (url-allowlist.ts) — сверяет тест.
    urlSchemeNotAllowed:
      'Ссылка должна начинаться с http://, https:// или mailto:',

    logoutServerUnreachable: 'Выход выполнен локально, связь с сервером не удалась.',
  },
);
